/**
 * CSV export helper (A1).
 *
 * Self-contained — no papaparse dep needed for the table shapes we use.
 * Outputs a BOM-prefixed UTF-8 CSV (Excel renders Indian rupee symbols
 * correctly with the BOM). Filename gets today's date appended in
 * YYYY-MM-DD so re-exports don't collide.
 *
 * Usage:
 *   exportToCSV(rows, 'users', [
 *     { field: 'name',  header: 'Name' },
 *     { field: 'email', header: 'Email' },
 *   ])
 */

const escapeCell = (val) => {
    if (val === null || val === undefined) return ''
    let s = String(val)
    // Always escape — keeps the rules simple and the output safe.
    if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r')) {
        s = `"${s.replace(/"/g, '""')}"`
    }
    return s
}

/**
 * Pick a column → row value, walking a dot-path if `field` is nested
 * (`customer.gstin`). Returns '' for missing keys.
 */
const readField = (row, field) => {
    if (!field) return ''
    const parts = String(field).split('.')
    let cur = row
    for (const p of parts) {
        if (cur == null) return ''
        cur = cur[p]
    }
    return cur
}

export const exportToCSV = (rows, filename, columns) => {
    if (!Array.isArray(rows) || rows.length === 0) return

    // Filter out non-data columns (action / select / icon-only buttons).
    const exportable = (columns || []).filter(
        (c) => c && c.field && c.field !== 'action' && c.exportable !== false
    )

    const headerLine = exportable.length
        ? exportable.map((c) => escapeCell(c.header || c.field)).join(',')
        : Object.keys(rows[0]).map(escapeCell).join(',')

    const bodyLines = rows.map((row) => {
        if (exportable.length) {
            return exportable.map((c) => escapeCell(readField(row, c.field))).join(',')
        }
        return Object.values(row).map(escapeCell).join(',')
    })

    const csv = [headerLine, ...bodyLines].join('\r\n')

    // ﻿ BOM — needed for Excel to detect UTF-8 correctly.
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    try {
        const link = document.createElement('a')
        link.href = url
        const date = new Date().toISOString().split('T')[0]
        link.download = `${filename || 'export'}_${date}.csv`
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    } finally {
        setTimeout(() => URL.revokeObjectURL(url), 0)
    }
}

export default exportToCSV
